// Settings > Digital Waste Tracking > Product profiles (admin).
//
// For each product the company receives: the Defra waste profile (EWC code,
// description, physical form, container, hazard property codes, disposal or recovery
// codes, default consignment reason, default weight). Nothing is reported until an
// admin CONFIRMS a profile; editing a confirmed profile resets the confirmation.
// Code lists show the code with OUR wording, never Defra's descriptions.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Save, ShieldCheck } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Badge } from '../../../components/ui/Badge/Badge';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { Modal } from '../../../components/ui/Modal/Modal';
import {
  confirmProfile,
  fetchDwtReference,
  fetchProfileProducts,
  fetchProfiles,
  lookupEwc,
  saveProfile,
  unconfirmProfile,
  type DwtComponentSource,
  type DwtEwcLookup,
  type DwtPhysicalForm,
  type DwtProfile,
  type DwtProfileInput,
  type DwtProfileProduct,
  type DwtReference,
  type NoConsignmentReasonCode,
} from '../../../api/dwt';
import { useAuthStore } from '../../../store/authStore';
import { useToast } from '../../../hooks/useToast';
import { formatDateTime } from '../../dwt/dwtLabels';
import shared from '../../dwt/dwt.module.css';

const SOURCE_FALLBACK: Array<{ value: DwtComponentSource; label: string }> = [
  { value: 'NOT_PROVIDED', label: 'Not provided' },
  { value: 'PROVIDED_WITH_WASTE', label: 'Provided with the waste' },
  { value: 'GUIDANCE', label: 'Taken from guidance' },
  { value: 'OWN_TESTING', label: 'From our own testing' },
];

export interface ProfileForm {
  ewc: string;
  description: string;
  physicalForm: DwtPhysicalForm;
  container: string;
  containers: string;
  hazardous: boolean;
  pops: boolean;
  hazCodes: string[];
  hazSource: DwtComponentSource;
  popsSource: DwtComponentSource;
  drCodes: string[];
  defaultWeight: string;
  consignmentReason: '' | NoConsignmentReasonCode;
}

export const BLANK_FORM: ProfileForm = {
  ewc: '',
  description: '',
  physicalForm: 'Solid',
  container: '',
  containers: '1',
  hazardous: false,
  pops: false,
  hazCodes: [],
  hazSource: 'NOT_PROVIDED',
  popsSource: 'NOT_PROVIDED',
  drCodes: [],
  defaultWeight: '',
  consignmentReason: '',
};

export function profileToForm(p: DwtProfile): ProfileForm {
  return {
    ewc: p.ewc_codes.join(', '),
    description: p.waste_description_template,
    physicalForm: p.physical_form,
    container: p.container_type ?? '',
    containers: String(p.number_of_containers_default),
    hazardous: p.contains_hazardous,
    pops: p.contains_pops,
    hazCodes: [...p.hazard_property_codes],
    hazSource: p.hazardous_source_of_components,
    popsSource: p.pops_source_of_components,
    drCodes: [...p.disposal_recovery_codes],
    defaultWeight: p.default_weight_kg === null ? '' : String(p.default_weight_kg),
    consignmentReason: p.default_consignment_reason ?? '',
  };
}

export function parseEwc(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((c) => c.trim())
    .filter((c) => c !== '');
}

/** Same rules as the server (profileSchema + saveProfile), so mistakes show before saving. */
export function validateProfile(f: ProfileForm): Partial<Record<keyof ProfileForm, string>> {
  const e: Partial<Record<keyof ProfileForm, string>> = {};
  const codes = parseEwc(f.ewc);
  if (codes.length === 0) e.ewc = 'Enter at least one EWC code.';
  else if (codes.length > 5) e.ewc = 'At most 5 EWC codes.';
  else if (codes.some((c) => !/^\d{6}$/.test(c))) e.ewc = 'EWC codes are six digits, for example 160104.';
  if (f.description.trim() === '') e.description = 'Enter a waste description.';
  const n = Number(f.containers);
  if (!Number.isInteger(n) || n < 0 || n > 100000) e.containers = 'Enter a whole number of containers.';
  if (f.defaultWeight.trim() !== '') {
    const w = Number(f.defaultWeight);
    if (!Number.isFinite(w) || w <= 0 || w > 1_000_000) e.defaultWeight = 'Weight must be greater than 0.';
  }
  if (f.consignmentReason !== '' && !f.hazardous) {
    e.consignmentReason = 'A default consignment reason only applies to hazardous waste.';
  }
  return e;
}

export function formToInput(f: ProfileForm): DwtProfileInput {
  return {
    ewc_codes: parseEwc(f.ewc),
    waste_description_template: f.description.trim(),
    physical_form: f.physicalForm,
    container_type: f.container.trim() === '' ? null : f.container.trim(),
    number_of_containers_default: Number(f.containers),
    contains_hazardous: f.hazardous,
    contains_pops: f.pops,
    hazard_property_codes: f.hazardous ? f.hazCodes : [],
    hazardous_source_of_components: f.hazSource,
    pops_source_of_components: f.popsSource,
    disposal_recovery_codes: f.drCodes,
    default_weight_kg: f.defaultWeight.trim() === '' ? null : Number(f.defaultWeight),
    default_weight_is_estimate: true,
    default_consignment_reason: f.hazardous && f.consignmentReason !== '' ? f.consignmentReason : null,
  };
}

function sameForm(a: ProfileForm, b: ProfileForm): boolean {
  return JSON.stringify(formToInput(a)) === JSON.stringify(formToInput(b));
}

const STATUS_BADGE: Record<DwtProfileProduct['status'], { label: string; variant: 'success' | 'warning' | 'muted' }> = {
  confirmed: { label: 'Confirmed', variant: 'success' },
  unconfirmed: { label: 'Not confirmed', variant: 'warning' },
  none: { label: 'No profile', variant: 'muted' },
};

function labelOf(list: Array<{ code: string; label: string }> | undefined, code: string): string {
  const hit = list?.find((c) => c.code === code);
  return hit && hit.label !== code ? `${code} (${hit.label})` : code;
}

// -- code pickers -----------------------------------------------------------------------

function CodeChecklist(props: {
  legend: string;
  options: Array<{ code: string; label: string }>;
  value: string[];
  onChange: (v: string[]) => void;
}): JSX.Element {
  const { legend, options, value, onChange } = props;
  return (
    <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
      <legend className={shared.muted}>{legend}</legend>
      <div className={shared.checkGrid}>
        {options.map((o) => (
          <label key={o.code} className={shared.check}>
            <input
              type="checkbox"
              checked={value.includes(o.code)}
              onChange={(e) => onChange(e.target.checked ? [...value, o.code] : value.filter((c) => c !== o.code))}
            />
            <span>
              <strong>{o.code}</strong>
              {o.label !== o.code ? <span className={shared.muted}> {o.label}</span> : null}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function CodeText(props: { id: string; label: string; value: string[]; onChange: (v: string[]) => void }): JSX.Element {
  return (
    <div className={shared.field}>
      <label htmlFor={props.id}>{props.label}</label>
      <input id={props.id} value={props.value.join(', ')} onChange={(e) => props.onChange(parseEwc(e.target.value))} />
      <span className={shared.muted}>Defra’s code lists have not been downloaded yet, so type the codes separated by commas.</span>
    </div>
  );
}

// -- the editor ---------------------------------------------------------------------------

function ProfileEditor(props: { product: DwtProfileProduct; profile: DwtProfile | null; reference: DwtReference | undefined; onChanged: () => void }): JSX.Element {
  const { product, profile, reference } = props;
  const toast = useToast();
  const userName = useAuthStore((s) => s.user?.name ?? 'You');
  const saved = useMemo(() => (profile ? profileToForm(profile) : BLANK_FORM), [profile]);
  const [form, setForm] = useState<ProfileForm>(saved);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [ewcInfo, setEwcInfo] = useState<DwtEwcLookup | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [justReset, setJustReset] = useState(false);

  useEffect(() => {
    setForm(saved);
    setServerErrors({});
    setEwcInfo(null);
  }, [saved, product.product_enc_id]);

  useEffect(() => {
    setJustReset(false);
  }, [product.product_enc_id]);

  const errors = validateProfile(form);
  const hasErrors = Object.keys(errors).length > 0;
  const dirty = !sameForm(form, saved);
  const err = (k: keyof ProfileForm) => errors[k] ?? serverErrors[k];
  const set = <K extends keyof ProfileForm>(k: K, v: ProfileForm[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setServerErrors((e) => ({ ...e, [k]: '' }));
  };

  async function checkEwc() {
    const codes = parseEwc(form.ewc).filter((c) => /^\d{6}$/.test(c)).slice(0, 10);
    if (codes.length === 0) {
      setEwcInfo(null);
      return;
    }
    try {
      setEwcInfo(await lookupEwc(codes));
    } catch {
      setEwcInfo(null);
    }
  }

  const hazardousEwc = ewcInfo ? Object.entries(ewcInfo.codes).filter(([, v]) => v.hazardous === true).map(([c]) => c) : [];
  const unknownEwc = ewcInfo ? Object.entries(ewcInfo.codes).filter(([, v]) => v.known === false).map(([c]) => c) : [];

  const save = useMutation({
    mutationFn: () => saveProfile(product.product_enc_id, formToInput(form)),
    onSuccess: () => {
      if (profile?.confirmed) {
        setJustReset(true);
        toast.warning('Saved. The confirmation was reset: confirm the profile again before receipts for this product are reported.', 9000);
      } else toast.success('Profile saved.');
      props.onChanged();
    },
    onError: (e) => {
      const data = (e as { response?: { data?: { message?: string; fields?: Record<string, string> } } } | null)?.response?.data;
      const map: Record<string, keyof ProfileForm> = {
        ewc_codes: 'ewc',
        hazard_property_codes: 'hazCodes',
        disposal_recovery_codes: 'drCodes',
        container_type: 'container',
        default_consignment_reason: 'consignmentReason',
      };
      const mapped: Record<string, string> = {};
      for (const [k, v] of Object.entries(data?.fields ?? {})) mapped[map[k] ?? k] = v;
      setServerErrors(mapped);
      toast.error(Object.keys(mapped).length > 0 ? 'Please correct the highlighted details.' : (data?.message ?? 'Could not save the profile.'));
    },
  });

  const confirm = useMutation({
    mutationFn: () => confirmProfile(product.product_enc_id),
    onSuccess: () => {
      toast.success('Profile confirmed. Receipts for this product can now be reported.');
      setConfirmOpen(false);
      setJustReset(false);
      props.onChanged();
    },
    onError: (e) => {
      const msg = (e as { response?: { data?: { message?: string } } } | null)?.response?.data?.message;
      toast.error(msg ?? 'Could not confirm the profile.');
    },
  });

  const unconfirm = useMutation({
    mutationFn: () => unconfirmProfile(product.product_enc_id),
    onSuccess: () => {
      toast.info('Confirmation removed. Receipts for this product will not be reported until it is confirmed again.');
      props.onChanged();
    },
    onError: () => toast.error('Could not remove the confirmation.'),
  });

  const cached = reference?.cached === true;
  const sources = reference?.sources.length ? reference.sources.map((s) => ({ value: s.value as DwtComponentSource, label: s.label })) : SOURCE_FALLBACK;
  const blockers = profile?.confirm_blockers ?? [];
  const status = profile === null ? 'none' : profile.confirmed ? 'confirmed' : 'unconfirmed';

  return (
    <Card title={`Waste profile: ${product.product_name}`}>
      <div className={shared.stack}>
        <div className={shared.row}>
          <Badge variant={STATUS_BADGE[status].variant}>{STATUS_BADGE[status].label}</Badge>
          {profile?.confirmed ? (
            <span className={shared.muted}>
              Confirmed by {profile.confirmed_by_name ?? 'an admin'} on {formatDateTime(profile.confirmed_at)}
            </span>
          ) : null}
        </div>

        {justReset ? (
          <div className={`${shared.banner} ${shared.bannerWarning}`} role="alert">
            <span className={shared.bannerTitle}>
              <AlertTriangle size={16} aria-hidden /> Confirmation reset
            </span>
            <span>
              You changed a confirmed profile, so it needs to be confirmed again. Receipts for this product are not reported
              until then.
            </span>
          </div>
        ) : profile?.confirmed && dirty ? (
          <div className={`${shared.banner} ${shared.bannerWarning}`} role="status">
            <span>Saving these changes will reset the confirmation. You will need to confirm the profile again.</span>
          </div>
        ) : null}

        <div className={shared.fieldGrid}>
          <div className={shared.field}>
            <label htmlFor="pf-ewc">EWC code(s)</label>
            <input
              id="pf-ewc"
              value={form.ewc}
              onChange={(e) => set('ewc', e.target.value)}
              onBlur={() => void checkEwc()}
              placeholder="160104"
              aria-invalid={err('ewc') ? true : undefined}
            />
            {err('ewc') ? <span className={shared.fieldError}>{err('ewc')}</span> : <span className={shared.muted}>Six digits, up to 5, separated by commas.</span>}
            {unknownEwc.length > 0 ? (
              <span className={shared.fieldError}>Not in Defra’s list: {unknownEwc.join(', ')}.</span>
            ) : null}
            {hazardousEwc.length > 0 && !form.hazardous ? (
              <span className={shared.fieldError}>
                {hazardousEwc.join(', ')} {hazardousEwc.length === 1 ? 'is' : 'are'} hazardous waste. Tick “Contains hazardous waste” and choose hazard property codes.
              </span>
            ) : null}
          </div>

          <div className={shared.field}>
            <label htmlFor="pf-form">Physical form</label>
            <select id="pf-form" value={form.physicalForm} onChange={(e) => set('physicalForm', e.target.value as DwtPhysicalForm)}>
              {(reference?.physical_forms ?? ['Solid', 'Liquid', 'Powder', 'Sludge', 'Gas', 'Mixed']).map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>

          <div className={shared.field}>
            <label htmlFor="pf-container">Container type</label>
            {cached ? (
              <select id="pf-container" value={form.container} onChange={(e) => set('container', e.target.value)} aria-invalid={err('container') ? true : undefined}>
                <option value="">Choose…</option>
                {reference!.container_types.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label === c.code ? c.code : `${c.label} (${c.code})`}
                  </option>
                ))}
              </select>
            ) : (
              <input id="pf-container" value={form.container} onChange={(e) => set('container', e.target.value)} />
            )}
            {err('container') ? <span className={shared.fieldError}>{err('container')}</span> : null}
          </div>

          <div className={shared.field}>
            <label htmlFor="pf-containers">Number of containers (default)</label>
            <input id="pf-containers" inputMode="numeric" value={form.containers} onChange={(e) => set('containers', e.target.value)} aria-invalid={err('containers') ? true : undefined} />
            {err('containers') ? <span className={shared.fieldError}>{err('containers')}</span> : null}
          </div>
        </div>

        <div className={shared.field}>
          <label htmlFor="pf-desc">Waste description</label>
          <textarea id="pf-desc" rows={2} value={form.description} onChange={(e) => set('description', e.target.value)} aria-invalid={err('description') ? true : undefined} />
          {err('description') ? <span className={shared.fieldError}>{err('description')}</span> : null}
          <span className={shared.muted}>
            You can use {'{registration}'} and {'{make_model}'}; they are filled in from each receipt.
          </span>
        </div>

        <div className={shared.row}>
          <label className={shared.check}>
            <input type="checkbox" checked={form.hazardous} onChange={(e) => set('hazardous', e.target.checked)} />
            Contains hazardous waste
          </label>
          <label className={shared.check}>
            <input type="checkbox" checked={form.pops} onChange={(e) => set('pops', e.target.checked)} />
            Contains persistent organic pollutants (POPs)
          </label>
        </div>

        {form.hazardous ? (
          <>
            {cached ? (
              <CodeChecklist
                legend="Hazard property codes"
                options={reference!.hazard_property_codes}
                value={form.hazCodes}
                onChange={(v) => set('hazCodes', v)}
              />
            ) : (
              <CodeText id="pf-hp" label="Hazard property codes" value={form.hazCodes} onChange={(v) => set('hazCodes', v)} />
            )}
            {err('hazCodes') ? <span className={shared.fieldError}>{err('hazCodes')}</span> : null}
            <div className={shared.fieldGrid}>
              <div className={shared.field}>
                <label htmlFor="pf-hazsource">Where the hazardous components are known from</label>
                <select id="pf-hazsource" value={form.hazSource} onChange={(e) => set('hazSource', e.target.value as DwtComponentSource)}>
                  {sources.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className={shared.field}>
                <label htmlFor="pf-reason">Default consignment reason</label>
                <select id="pf-reason" value={form.consignmentReason} onChange={(e) => set('consignmentReason', e.target.value as ProfileForm['consignmentReason'])} aria-invalid={err('consignmentReason') ? true : undefined}>
                  <option value="">None</option>
                  {(reference?.no_consignment_reasons ?? []).map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
                {err('consignmentReason') ? <span className={shared.fieldError}>{err('consignmentReason')}</span> : null}
                <span className={shared.muted}>Used when a receipt has no consignment note code or reason of its own.</span>
              </div>
            </div>
          </>
        ) : null}

        {form.pops ? (
          <div className={shared.field}>
            <label htmlFor="pf-popsource">Where the POPs components are known from</label>
            <select id="pf-popsource" value={form.popsSource} onChange={(e) => set('popsSource', e.target.value as DwtComponentSource)}>
              {sources.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {cached ? (
          <CodeChecklist legend="Disposal or recovery codes" options={reference!.disposal_recovery_codes} value={form.drCodes} onChange={(v) => set('drCodes', v)} />
        ) : (
          <CodeText id="pf-dr" label="Disposal or recovery codes" value={form.drCodes} onChange={(v) => set('drCodes', v)} />
        )}
        {err('drCodes') ? <span className={shared.fieldError}>{err('drCodes')}</span> : null}

        <div className={shared.fieldGrid}>
          <div className={shared.field}>
            <label htmlFor="pf-weight">Default weight (kg)</label>
            <input id="pf-weight" inputMode="decimal" value={form.defaultWeight} onChange={(e) => set('defaultWeight', e.target.value)} aria-invalid={err('defaultWeight') ? true : undefined} />
            {err('defaultWeight') ? <span className={shared.fieldError}>{err('defaultWeight')}</span> : null}
            <label className={shared.check}>
              <input type="checkbox" checked disabled readOnly aria-label="Default weight is an estimate" />
              Estimate (always)
            </label>
            <span className={shared.muted}>
              Used when a receipt has no weight of its own. A defaulted weight is always reported to Defra as an estimate.
            </span>
          </div>
        </div>

        <div className={shared.row}>
          <Button icon={Save} onClick={() => save.mutate()} loading={save.isPending} disabled={!dirty || hasErrors}>
            Save profile
          </Button>
          <Button
            variant="secondary"
            icon={ShieldCheck}
            onClick={() => setConfirmOpen(true)}
            disabled={profile === null || profile.confirmed || dirty || blockers.length > 0}
            title={dirty ? 'Save your changes first' : undefined}
          >
            Confirm profile
          </Button>
          {profile?.confirmed ? (
            <Button variant="ghost" onClick={() => unconfirm.mutate()} loading={unconfirm.isPending}>
              Remove confirmation
            </Button>
          ) : null}
          {dirty ? <span className={shared.muted}>Unsaved changes</span> : null}
        </div>

        {profile !== null && !profile.confirmed && blockers.length > 0 ? (
          <div className={`${shared.banner} ${shared.bannerInfo}`} role="status">
            <span className={shared.bannerTitle}>It cannot be confirmed yet</span>
            <ul className={shared.list}>
              {blockers.map((b) => (
                <li key={b}>{b.charAt(0).toUpperCase() + b.slice(1)}.</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      <Modal
        open={confirmOpen && profile !== null}
        onClose={() => !confirm.isPending && setConfirmOpen(false)}
        title="Confirm this profile?"
        size="md"
        preventClose={confirm.isPending}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)} disabled={confirm.isPending}>
              Cancel
            </Button>
            <Button onClick={() => confirm.mutate()} loading={confirm.isPending}>
              Confirm profile
            </Button>
          </>
        }
      >
        {profile ? (
          <div className={shared.stack}>
            <p style={{ margin: 0 }}>
              You are confirming that this is correct for <strong>{product.product_name}</strong>. Once confirmed, receipts for
              this product are reported to Defra with exactly these details.
            </p>
            <dl className={shared.facts} aria-label="What will be confirmed">
              <dt className={shared.factLabel}>EWC code(s)</dt>
              <dd className={shared.factValue}>{profile.ewc_codes.join(', ')}</dd>
              <dt className={shared.factLabel}>Waste description</dt>
              <dd className={shared.factValue}>{profile.waste_description_template}</dd>
              <dt className={shared.factLabel}>Physical form</dt>
              <dd className={shared.factValue}>{profile.physical_form}</dd>
              <dt className={shared.factLabel}>Container</dt>
              <dd className={shared.factValue}>
                {profile.container_type ? labelOf(reference?.container_types, profile.container_type) : '—'} × {profile.number_of_containers_default}
              </dd>
              <dt className={shared.factLabel}>Hazardous</dt>
              <dd className={shared.factValue}>
                {profile.contains_hazardous
                  ? `Yes: ${profile.hazard_property_codes.map((c) => labelOf(reference?.hazard_property_codes, c)).join(', ') || 'no hazard codes'}`
                  : 'No'}
              </dd>
              <dt className={shared.factLabel}>POPs</dt>
              <dd className={shared.factValue}>{profile.contains_pops ? 'Yes' : 'No'}</dd>
              <dt className={shared.factLabel}>Disposal or recovery</dt>
              <dd className={shared.factValue}>
                {profile.disposal_recovery_codes.map((c) => labelOf(reference?.disposal_recovery_codes, c)).join(', ') || 'None'}
              </dd>
              <dt className={shared.factLabel}>Default weight</dt>
              <dd className={shared.factValue}>{profile.default_weight_kg === null ? 'None' : `${profile.default_weight_kg} kg (estimate)`}</dd>
              <dt className={shared.factLabel}>Default consignment reason</dt>
              <dd className={shared.factValue}>
                {profile.default_consignment_reason
                  ? (reference?.no_consignment_reasons.find((r) => r.value === profile.default_consignment_reason)?.label ?? profile.default_consignment_reason)
                  : 'None'}
              </dd>
            </dl>
            <p className={shared.muted} style={{ margin: 0 }}>
              Confirmed by <strong>{userName}</strong>, {formatDateTime(new Date().toISOString())}. This is recorded in the audit
              trail.
              {profile.confirmed_at
                ? ` Previously confirmed by ${profile.confirmed_by_name ?? 'an admin'} on ${formatDateTime(profile.confirmed_at)}.`
                : ''}
            </p>
          </div>
        ) : null}
      </Modal>
    </Card>
  );
}

// -- the page ---------------------------------------------------------------------------

export function DwtProfilesPage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const selected = params.get('product');

  const products = useQuery({ queryKey: ['dwt', 'profile-products'], queryFn: fetchProfileProducts, retry: false });
  const profiles = useQuery({ queryKey: ['dwt', 'profiles'], queryFn: fetchProfiles, retry: false });
  const reference = useQuery({ queryKey: ['dwt', 'reference'], queryFn: fetchDwtReference, retry: false, staleTime: 5 * 60_000 });

  const list = products.data ?? [];
  const product = list.find((p) => p.product_enc_id === selected) ?? null;
  const profile = product ? (profiles.data ?? []).find((p) => p.product_enc_id === product.product_enc_id) ?? null : null;
  const counts = {
    confirmed: list.filter((p) => p.status === 'confirmed').length,
    unconfirmed: list.filter((p) => p.status === 'unconfirmed').length,
    none: list.filter((p) => p.status === 'none').length,
  };

  function refresh() {
    void qc.invalidateQueries({ queryKey: ['dwt', 'profile-products'] });
    void qc.invalidateQueries({ queryKey: ['dwt', 'profiles'] });
  }

  return (
    <div className={shared.stack} style={{ padding: '0 1rem 3rem' }}>
      <PageHeader
        title="Product profiles"
        subtitle="What each product is called in Defra’s system. A product is only reported once its profile is confirmed."
        actions={
          <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/settings/dwt')}>
            Back
          </Button>
        }
      />

      {products.isLoading || profiles.isLoading ? (
        <Card>
          <div className={shared.row}>
            <Spinner /> <span>Loading…</span>
          </div>
        </Card>
      ) : products.isError || profiles.isError ? (
        <Card>
          <p role="alert">The product profiles could not be loaded.</p>
        </Card>
      ) : (
        <div className={shared.split}>
          <Card title="Products">
            <p className={shared.muted} style={{ marginTop: 0 }}>
              {counts.confirmed} confirmed · {counts.unconfirmed} to confirm · {counts.none} without a profile
            </p>
            {list.length === 0 ? (
              <p className={shared.muted}>There are no products yet.</p>
            ) : (
              <ul className={shared.stack} style={{ listStyle: 'none', padding: 0, margin: 0 }} aria-label="Products">
                {list.map((p) => (
                  <li key={p.product_enc_id}>
                    <button
                      type="button"
                      className={`${shared.productButton} ${p.product_enc_id === selected ? shared.productSelected : ''}`}
                      aria-pressed={p.product_enc_id === selected}
                      onClick={() => setParams({ product: p.product_enc_id })}
                    >
                      <span>{p.product_name}</span>
                      <Badge variant={STATUS_BADGE[p.status].variant}>{STATUS_BADGE[p.status].label}</Badge>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {product ? (
            <ProfileEditor key={product.product_enc_id} product={product} profile={profile} reference={reference.data} onChanged={refresh} />
          ) : (
            <Card>
              <p className={shared.muted}>Choose a product to view or edit its waste profile.</p>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

export default DwtProfilesPage;
